import * as React from "react";
import * as RechartsPrimitive from "recharts";
import { cn } from "~/lib/utils";

// Format: { THEME_NAME: CSS_SELECTOR }
const THEMES = { light: "", dark: ".dark" } as const;

export type ChartConfig = {
  [k in string]: {
    label?: React.ReactNode;
    icon?: React.ComponentType;
  } & (
    | { color?: string; theme?: never }
    | { color?: never; theme: Record<keyof typeof THEMES, string> }
  );
};

type ChartContextProps = {
  config: ChartConfig;
};

const ChartContext = React.createContext<ChartContextProps | null>(null);

function useChart() {
  const context = React.useContext(ChartContext);

  if (!context) {
    throw new Error("useChart must be used within a <ChartContainer />");
  }

  return context;
}

const ChartContainer = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    config: ChartConfig;
    children: React.ComponentProps<
      typeof RechartsPrimitive.ResponsiveContainer
    >["children"];
  }
>(({ id, className, children, config, ...props }, ref) => {
  const uniqueId = React.useId();
  const chartId = `chart-${id || uniqueId.replace(/:/g, "")}`;

  return (
    <ChartContext.Provider value={{ config }}>
      <div
        data-chart={chartId}
        ref={ref}
        className={cn(
          "flex aspect-video justify-center text-xs [&_.recharts-cartesian-axis-tick_text]:fill-[#7F8A9A] [&_.recharts-legend-item_text]:fill-[#7F8A9A] [&_.recharts-cartesian-grid_line]:stroke-[rgba(0,0,0,0.04)] [&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-[rgba(0,0,0,0.04)] [&_.recharts-curve.recharts-tooltip-cursor]:stroke-border [&_.recharts-dot[stroke='#fff']]:stroke-transparent [&_.recharts-layer]:outline-none [&_.recharts-polar-grid_[stroke='#ccc']]:stroke-border [&_.recharts-radial-bar-background-sector]:fill-muted [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted [&_.recharts-reference-line_[stroke='#ccc']]:stroke-border [&_.recharts-sector[stroke='#fff']]:stroke-transparent [&_.recharts-sector]:outline-none [&_.recharts-surface]:outline-none [&_.recharts-text]:fill-[#7F8A9A] [&_.recharts-label]:fill-[#202A36]",
          className,
        )}
        style={{ filter: "drop-shadow(0 2px 10px rgba(30,45,60,0.05))", padding: "24px", ...props.style }}
        {...props}
      >
        <svg width="0" height="0" style={{ position: "absolute", pointerEvents: "none" }}>
          <defs>
            <linearGradient id="limeGreenGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#D8FF00" />
              <stop offset="45%" stopColor="#CBFF00" />
              <stop offset="100%" stopColor="#B9FF00" />
            </linearGradient>
            <linearGradient id="deepBlueGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#315B78" />
              <stop offset="45%" stopColor="#2B546F" />
              <stop offset="100%" stopColor="#23475F" />
            </linearGradient>
            <linearGradient id="skyBlueGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#76B8E8" />
              <stop offset="100%" stopColor="#509BD2" />
            </linearGradient>
            <linearGradient id="veilighTealGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3E8D89" />
              <stop offset="100%" stopColor="#2B6E6A" />
            </linearGradient>
            <linearGradient id="slateGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#5E7288" />
              <stop offset="100%" stopColor="#455567" />
            </linearGradient>
            <linearGradient id="steelBlueGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#4C7A9B" />
              <stop offset="100%" stopColor="#345C78" />
            </linearGradient>
            <linearGradient id="sageGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#7BAA78" />
              <stop offset="100%" stopColor="#5C8E58" />
            </linearGradient>
            <linearGradient id="amberGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#D8A83A" />
              <stop offset="100%" stopColor="#B2831E" />
            </linearGradient>
            <linearGradient id="coralGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#D96C5F" />
              <stop offset="100%" stopColor="#B24F44" />
            </linearGradient>
            <linearGradient id="plumGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#6E5E9E" />
              <stop offset="100%" stopColor="#4E3F7C" />
            </linearGradient>

            <linearGradient id="limeGreenGradientH" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#D8FF00" />
              <stop offset="45%" stopColor="#CBFF00" />
              <stop offset="100%" stopColor="#B9FF00" />
            </linearGradient>
            <linearGradient id="deepBlueGradientH" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#315B78" />
              <stop offset="45%" stopColor="#2B546F" />
              <stop offset="100%" stopColor="#23475F" />
            </linearGradient>
            <linearGradient id="skyBlueGradientH" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#76B8E8" />
              <stop offset="100%" stopColor="#509BD2" />
            </linearGradient>
            <linearGradient id="veilighTealGradientH" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#3E8D89" />
              <stop offset="100%" stopColor="#2B6E6A" />
            </linearGradient>
            <linearGradient id="slateGradientH" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#5E7288" />
              <stop offset="100%" stopColor="#455567" />
            </linearGradient>
            <linearGradient id="steelBlueGradientH" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#4C7A9B" />
              <stop offset="100%" stopColor="#345C78" />
            </linearGradient>
            <linearGradient id="sageGradientH" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#7BAA78" />
              <stop offset="100%" stopColor="#5C8E58" />
            </linearGradient>
            <linearGradient id="amberGradientH" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#D8A83A" />
              <stop offset="100%" stopColor="#B2831E" />
            </linearGradient>
            <linearGradient id="coralGradientH" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#D96C5F" />
              <stop offset="100%" stopColor="#B24F44" />
            </linearGradient>
            <linearGradient id="plumGradientH" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#6E5E9E" />
              <stop offset="100%" stopColor="#4E3F7C" />
            </linearGradient>
          </defs>
        </svg>
        <ChartStyle id={chartId} config={config} />
        <RechartsPrimitive.ResponsiveContainer>
          {children}
        </RechartsPrimitive.ResponsiveContainer>
      </div>
    </ChartContext.Provider>
  );
});
ChartContainer.displayName = "Chart";

const ChartStyle = ({ id, config }: { id: string; config: ChartConfig }) => {
  const colorConfig = Object.entries(config).filter(
    ([, config]) => config.theme || config.color,
  );

  if (!colorConfig.length) {
    return null;
  }

  return (
    <style
      dangerouslySetInnerHTML={{
        __html: Object.entries(THEMES)
          .map(
            ([theme, prefix]) => `
${prefix} [data-chart=${id}] {
${colorConfig
  .map(([key, itemConfig]) => {
    const color =
      itemConfig.theme?.[theme as keyof typeof itemConfig.theme] ||
      itemConfig.color;
    const finalColor = color && color.startsWith("var(--chart-") ? `hsl(${color})` : color;
    return finalColor ? `  --color-${key}: ${finalColor};` : null;
  })
  .join("\n")}
}
`,
          )
          .join("\n"),
      }}
    />
  );
};

const ChartTooltip = RechartsPrimitive.Tooltip;

const ChartTooltipContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof RechartsPrimitive.Tooltip> &
    React.ComponentProps<"div"> & {
      hideLabel?: boolean;
      hideIndicator?: boolean;
      indicator?: "line" | "dot" | "dashed";
      nameKey?: string;
      labelKey?: string;
    }
>(
  (
    {
      active,
      payload,
      className,
      indicator = "dot",
      hideLabel = false,
      hideIndicator = false,
      label,
      labelFormatter,
      labelClassName,
      formatter,
      color,
      nameKey,
      labelKey,
    },
    ref,
  ) => {
    const { config } = useChart();

    const tooltipLabel = React.useMemo(() => {
      if (hideLabel || !payload?.length) {
        return null;
      }

      const [item] = payload;
      const key = `${labelKey || item.dataKey || item.name || "value"}`;
      const itemConfig = getPayloadConfigFromPayload(config, item, key);
      const value =
        !labelKey && typeof label === "string"
          ? config[label as keyof typeof config]?.label || label
          : itemConfig?.label;

      if (labelFormatter) {
        return (
          <div className={cn("font-medium", labelClassName)}>
            {labelFormatter(value, payload)}
          </div>
        );
      }

      if (!value) {
        return null;
      }

      return <div className={cn("font-medium", labelClassName)}>{value}</div>;
    }, [
      label,
      labelFormatter,
      payload,
      hideLabel,
      labelClassName,
      config,
      labelKey,
    ]);

    if (!active || !payload?.length) {
      return null;
    }

    const nestLabel = payload.length === 1 && indicator !== "dot";

    return (
      <div
        ref={ref}
        className={cn(
          "grid min-w-[8rem] items-start gap-1.5 rounded-2xl border-none bg-background px-4 py-3 text-xs shadow-[0_8px_24px_rgba(0,0,0,0.08)]",
          className,
        )}
      >
        {!nestLabel ? tooltipLabel : null}
        <div className="grid gap-1.5">
          {payload.map((item, index) => {
            const key = `${nameKey || item.name || item.dataKey || "value"}`;
            const itemConfig = getPayloadConfigFromPayload(config, item, key);
            const indicatorColor = color || itemConfig?.color || item.payload.fill || item.color;

            return (
              <div
                key={item.dataKey}
                className={cn(
                  "flex w-full flex-wrap items-stretch gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5 [&>svg]:text-muted-foreground",
                  indicator === "dot" && "items-center",
                )}
              >
                {formatter && item?.value !== undefined && item.name ? (
                  formatter(item.value, item.name, item, index, item.payload)
                ) : (
                  <>
                    {itemConfig?.icon ? (
                      <itemConfig.icon />
                    ) : (
                      !hideIndicator && (
                        <div
                          className={cn(
                            "shrink-0 rounded-[2px] border-[--color-border] bg-[--color-bg]",
                            {
                              "h-2.5 w-2.5": indicator === "dot",
                              "w-1": indicator === "line",
                              "w-0 border-[1.5px] border-dashed bg-transparent":
                                indicator === "dashed",
                              "my-0.5": nestLabel && indicator === "dashed",
                            },
                          )}
                          style={
                            {
                              "--color-bg": indicatorColor,
                              "--color-border": indicatorColor,
                            } as React.CSSProperties
                          }
                        />
                      )
                    )}
                    <div
                      className={cn(
                        "flex flex-1 leading-none",
                        payload.length === 1
                          ? "items-center gap-1.5"
                          : cn("justify-between", nestLabel ? "items-end" : "items-center"),
                      )}
                    >
                      <div className="grid gap-1.5">
                        {nestLabel ? tooltipLabel : null}
                        <span className="text-muted-foreground">
                          {(() => {
                            const payloadName = item.payload?.name;
                            if (item.name === "value" || item.dataKey === "value") {
                              if (payloadName) {
                                return (
                                  config[payloadName]?.label ||
                                  payloadName.replace(/_/g, " ")
                                );
                              }
                            }
                            if (item.name && config[item.name as string] && item.name !== "value") {
                              return (
                                config[item.name as string]?.label ||
                                (item.name as string).replace(/_/g, " ")
                              );
                            }
                            if (payloadName) {
                              return (
                                config[payloadName]?.label ||
                                payloadName.replace(/_/g, " ")
                              );
                            }
                            return itemConfig?.label || item.name;
                          })()}
                        </span>
                      </div>
                      {payload.length === 1 && (
                        <span className="text-muted-foreground">-</span>
                      )}
                      {item.value !== undefined && (
                        <span className="font-mono font-medium tabular-nums text-foreground">
                          {item.value.toLocaleString()}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  },
);
ChartTooltipContent.displayName = "ChartTooltip";

const ChartLegend = RechartsPrimitive.Legend;

const ChartLegendContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> &
    Pick<RechartsPrimitive.LegendProps, "payload" | "verticalAlign"> & {
      hideIcon?: boolean;
      nameKey?: string;
    }
>(
  (
    { className, hideIcon = false, payload, verticalAlign = "bottom", nameKey },
    ref,
  ) => {
    const { config } = useChart();

    if (!payload?.length || payload.length <= 1) {
      return null;
    }

    return (
      <div
        ref={ref}
        className={cn(
          "flex items-center justify-center gap-4",
          verticalAlign === "top" ? "pb-3" : "pt-3",
          className,
        )}
      >
        {payload.map((item) => {
          const key = `${nameKey || item.dataKey || "value"}`;
          const itemConfig = getPayloadConfigFromPayload(config, item, key);

          return (
            <div
              key={item.value}
              className={cn(
                "flex items-center gap-1.5 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:text-muted-foreground",
              )}
            >
              {itemConfig?.icon && !hideIcon ? (
                <itemConfig.icon />
              ) : (
                <div
                  className="h-2 w-2 shrink-0 rounded-[2px]"
                  style={{
                    backgroundColor: itemConfig?.color || item.color,
                  }}
                />
              )}
              {itemConfig?.label}
            </div>
          );
        })}
      </div>
    );
  },
);
ChartLegendContent.displayName = "ChartLegend";

// Helper to extract item config from a payload.
function getPayloadConfigFromPayload(
  config: ChartConfig,
  payload: unknown,
  key: string,
) {
  if (typeof payload !== "object" || payload === null) {
    return undefined;
  }

  const payloadPayload =
    "payload" in payload &&
    typeof payload.payload === "object" &&
    payload.payload !== null
      ? payload.payload
      : undefined;

  let configLabelKey: string = key;

  if (
    key in payload &&
    typeof payload[key as keyof typeof payload] === "string"
  ) {
    configLabelKey = payload[key as keyof typeof payload] as string;
  } else if (
    payloadPayload &&
    key in payloadPayload &&
    typeof payloadPayload[key as keyof typeof payloadPayload] === "string"
  ) {
    configLabelKey = payloadPayload[
      key as keyof typeof payloadPayload
    ] as string;
  }

  return configLabelKey in config
    ? config[configLabelKey]
    : config[key as keyof typeof config];
}

export {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  ChartStyle,
};
